/* ============================================
   BASE JS — PubSub, AJAX helpers, custom elements
   ============================================ */

/* PubSub event bus for cross-component communication */
const PubSub = (() => {
  const events = {};

  return {
    subscribe(event, callback) {
      if (!events[event]) events[event] = [];
      events[event].push(callback);
      return () => {
        events[event] = events[event].filter(cb => cb !== callback);
      };
    },

    publish(event, data) {
      if (!events[event]) return;
      events[event].forEach(callback => callback(data));
    }
  };
})();

/* AJAX fetch helpers */
function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': `application/${type}`
    }
  };
}

async function fetchHTML(url) {
  const response = await fetch(url);
  return await response.text();
}

async function fetchJSON(url, config = {}) {
  const response = await fetch(url, config);
  return await response.json();
}

/* Shopify Cart API helpers */
const CartAPI = {
  async get() {
    return fetchJSON('/cart.js');
  },

  async add(items) {
    const config = fetchConfig();
    config.body = JSON.stringify({ items });
    const response = await fetch('/cart/add.js', config);
    const data = await response.json();
    PubSub.publish('cart:update', data);
    return data;
  },

  async update(updates) {
    const config = fetchConfig();
    config.body = JSON.stringify({ updates });
    const response = await fetch('/cart/update.js', config);
    const data = await response.json();
    PubSub.publish('cart:update', data);
    return data;
  },

  async change(line, quantity) {
    const config = fetchConfig();
    config.body = JSON.stringify({ line, quantity });
    const response = await fetch('/cart/change.js', config);
    const data = await response.json();
    PubSub.publish('cart:update', data);
    return data;
  }
};

/* Debounce */
function debounce(fn, wait) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), wait);
  };
}

/* Throttle */
function throttle(fn, limit) {
  let inThrottle;
  return (...args) => {
    if (!inThrottle) {
      fn.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

/* Scroll animation observer */
function initScrollAnimations() {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('animate-in');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.1 });

  document.querySelectorAll('[data-animate]').forEach(el => {
    observer.observe(el);
  });
}

/* Trap focus in modals/drawers */
function trapFocus(container, elementToFocus) {
  const focusableElements = container.querySelectorAll(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  );
  const firstFocusable = focusableElements[0];
  const lastFocusable = focusableElements[focusableElements.length - 1];

  if (elementToFocus) elementToFocus.focus();

  container.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return;

    if (e.shiftKey) {
      if (document.activeElement === firstFocusable) {
        lastFocusable.focus();
        e.preventDefault();
      }
    } else {
      if (document.activeElement === lastFocusable) {
        firstFocusable.focus();
        e.preventDefault();
      }
    }
  });
}

/* ============================================
   Custom Elements
   ============================================ */

/* Sticky Header */
class StickyHeader extends HTMLElement {
  connectedCallback() {
    this.isSticky = this.dataset.sticky === 'true';
    if (!this.isSticky) return;

    this.header = this.querySelector('.header');
    this.currentScrollTop = 0;

    this.onScroll = throttle(() => this.handleScroll(), 100);
    window.addEventListener('scroll', this.onScroll, { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
  }

  handleScroll() {
    const scrollTop = window.scrollY;

    if (scrollTop > 100) {
      this.classList.add('is-sticky');
    } else {
      this.classList.remove('is-sticky');
    }

    if (scrollTop > this.currentScrollTop && scrollTop > 200) {
      this.classList.add('scroll-down');
      this.classList.remove('scroll-up');
    } else {
      this.classList.remove('scroll-down');
      this.classList.add('scroll-up');
    }

    this.currentScrollTop = scrollTop;
  }
}

if (!customElements.get('sticky-header')) {
  customElements.define('sticky-header', StickyHeader);
}

/* ============================================
   Init on DOM ready
   ============================================ */

document.addEventListener('DOMContentLoaded', () => {
  initScrollAnimations();

  /* Mobile menu toggle */
  const menuToggle = document.querySelector('.header__menu-toggle');
  const mobileMenu = document.getElementById('MobileMenu');
  const mobileMenuOverlay = document.getElementById('MobileMenuOverlay');
  const mobileMenuClose = document.querySelector('.mobile-menu-drawer__close');

  function openMobileMenu() {
    if (!mobileMenu) return;
    mobileMenu.setAttribute('aria-hidden', 'false');
    mobileMenu.classList.add('is-open');
    mobileMenuOverlay.classList.add('is-visible');
    document.body.classList.add('overflow-hidden');
    if (menuToggle) menuToggle.setAttribute('aria-expanded', 'true');
    trapFocus(mobileMenu);
  }

  function closeMobileMenu() {
    if (!mobileMenu) return;
    mobileMenu.setAttribute('aria-hidden', 'true');
    mobileMenu.classList.remove('is-open');
    mobileMenuOverlay.classList.remove('is-visible');
    document.body.classList.remove('overflow-hidden');
    if (menuToggle) menuToggle.setAttribute('aria-expanded', 'false');
    if (menuToggle) menuToggle.focus();
  }

  if (menuToggle) menuToggle.addEventListener('click', openMobileMenu);
  if (mobileMenuClose) mobileMenuClose.addEventListener('click', closeMobileMenu);
  if (mobileMenuOverlay) mobileMenuOverlay.addEventListener('click', closeMobileMenu);

  /* Search drawer toggle */
  const searchToggle = document.querySelector('.header__search-toggle');
  const searchDrawer = document.getElementById('SearchDrawer');
  const searchClose = document.querySelector('.search-drawer__close');
  const searchOverlay = document.getElementById('SearchOverlay');

  function openSearch() {
    if (!searchDrawer) return;
    searchDrawer.setAttribute('aria-hidden', 'false');
    searchDrawer.classList.add('is-open');
    if (searchOverlay) searchOverlay.classList.add('is-visible');
    document.body.classList.add('overflow-hidden');
    const input = searchDrawer.querySelector('.search-drawer__input');
    if (input) input.focus();
  }

  function closeSearch() {
    if (!searchDrawer) return;
    searchDrawer.setAttribute('aria-hidden', 'true');
    searchDrawer.classList.remove('is-open');
    if (searchOverlay) searchOverlay.classList.remove('is-visible');
    document.body.classList.remove('overflow-hidden');
    const results = document.getElementById('PredictiveSearchResults');
    if (results) results.innerHTML = '';
    const input = searchDrawer.querySelector('.search-drawer__input');
    if (input) input.value = '';
    if (searchToggle) searchToggle.focus();
  }

  if (searchToggle) searchToggle.addEventListener('click', openSearch);
  if (searchClose) searchClose.addEventListener('click', closeSearch);
  if (searchOverlay) searchOverlay.addEventListener('click', closeSearch);

  /* Close drawers on Escape */
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeMobileMenu();
      closeSearch();
    }
  });

  /* Update cart count on cart:update */
  PubSub.subscribe('cart:update', (cart) => {
    const countElements = document.querySelectorAll('[data-cart-count]');
    countElements.forEach(el => {
      el.textContent = cart.item_count;
      if (cart.item_count > 0) {
        el.classList.add('has-items');
      } else {
        el.classList.remove('has-items');
      }
    });
  });

});
